import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from 'react';
import { on, removeListener, run } from '../helper/electron';
import log from '../utils/logger';

// Types for import progress tracking
export interface ImportJob {
  id: string;
  type: 'folder' | 'serato' | 'virtualdj';
  name: string;
  path?: string;
  status:
    | 'queued'
    | 'importing'
    | 'paused'
    | 'complete'
    | 'error'
    | 'cancelling'
    | 'cancelled';
  progress: {
    phase: string;
    totalTracks: number;
    processedTracks: number;
    message: string;
    created?: number;
    merged?: number;
    errors?: number;
    startTime?: number;
    elapsed?: number;
    estimatedTimeRemaining?: string;
    filesPerSecond?: string;
    totalDirectories?: number;
  };
  error?: string;
  addedAt: number;
  startedAt?: number;
  completedAt?: number;
}

interface ImportProgressState {
  jobs: Record<string, ImportJob>;
  queue: string[];
  activeJobs: string[];
  maxConcurrentJobs: number;
}

type ImportProgressAction =
  | { type: 'ADD_JOB'; payload: Omit<ImportJob, 'id' | 'addedAt'> }
  | {
      type: 'UPDATE_JOB_PROGRESS';
      payload: { id: string; progress: Partial<ImportJob['progress']> };
    }
  | {
      type: 'UPDATE_JOB_STATUS';
      payload: { id: string; status: ImportJob['status']; error?: string };
    }
  | { type: 'START_JOB'; payload: { id: string } }
  | { type: 'COMPLETE_JOB'; payload: { id: string } }
  | { type: 'CANCELLING_JOB'; payload: { id: string } }
  | { type: 'CANCEL_JOB'; payload: { id: string } }
  | { type: 'CLEAR_COMPLETED_JOBS' }
  | { type: 'PROCESS_QUEUE' };

const ImportProgressContext = createContext<{
  state: ImportProgressState;
  dispatch: React.Dispatch<ImportProgressAction>;
  addImportJob: (job: Omit<ImportJob, 'id' | 'addedAt'>) => string;
  getJobById: (id: string) => ImportJob | undefined;
  getActiveJobs: () => ImportJob[];
  getJobsForPath: (path: string) => ImportJob[];
} | null>(null);

const initialState: ImportProgressState = {
  jobs: {},
  queue: [],
  activeJobs: [],
  maxConcurrentJobs: 2, // Allow 2 concurrent imports max
};

function importProgressReducer(
  state: ImportProgressState,
  action: ImportProgressAction,
): ImportProgressState {
  switch (action.type) {
    case 'ADD_JOB': {
      const id = `import-${Date.now()}-${Math.random().toString(36).substring(7)}`;
      const newJob: ImportJob = {
        ...action.payload,
        id,
        addedAt: Date.now(),
        status: 'queued',
      };

      log.debug(
        `[ImportProgressContext] ADD_JOB reducer: Adding job ${id} to queue`,
      );

      return {
        ...state,
        jobs: { ...state.jobs, [id]: newJob },
        queue: [...state.queue, id],
      };
    }

    case 'UPDATE_JOB_PROGRESS': {
      const { id, progress } = action.payload;
      const job = state.jobs[id];
      if (!job) return state;

      return {
        ...state,
        jobs: {
          ...state.jobs,
          [id]: {
            ...job,
            progress: { ...job.progress, ...progress },
          },
        },
      };
    }

    case 'UPDATE_JOB_STATUS': {
      const { id, status, error } = action.payload;
      const job = state.jobs[id];
      if (!job) return state;

      const updates: Partial<ImportJob> = { status };
      if (error) updates.error = error;
      if (status === 'complete' || status === 'error') {
        updates.completedAt = Date.now();
      }
      if (status === 'importing' && !job.startedAt) {
        updates.startedAt = Date.now();
      }

      console.log(
        `[ImportProgressContext] UPDATE_JOB_STATUS: ${id} -> ${status}`,
        updates,
      );

      return {
        ...state,
        jobs: {
          ...state.jobs,
          [id]: { ...job, ...updates },
        },
      };
    }

    case 'START_JOB': {
      const { id } = action.payload;
      const job = state.jobs[id];
      if (!job) return state;

      return {
        ...state,
        jobs: {
          ...state.jobs,
          [id]: {
            ...job,
            status: 'importing',
            startedAt: Date.now(),
          },
        },
        queue: state.queue.filter((qId) => qId !== id),
        activeJobs: [...state.activeJobs, id],
      };
    }

    case 'COMPLETE_JOB': {
      const { id } = action.payload;
      return {
        ...state,
        activeJobs: state.activeJobs.filter((jobId) => jobId !== id),
      };
    }

    case 'CANCELLING_JOB': {
      const { id } = action.payload;
      const job = state.jobs[id];
      if (!job) return state;
      return {
        ...state,
        jobs: {
          ...state.jobs,
          [id]: { ...job, status: 'cancelling' },
        },
      };
    }

    case 'CANCEL_JOB': {
      const { id } = action.payload;
      return {
        ...state,
        queue: state.queue.filter((qId) => qId !== id),
        activeJobs: state.activeJobs.filter((jobId) => jobId !== id),
        jobs: {
          ...state.jobs,
          [id]: {
            ...state.jobs[id],
            status: 'cancelled',
            completedAt: Date.now(),
          },
        },
      };
    }

    case 'CLEAR_COMPLETED_JOBS': {
      const activeJobIds = new Set([...state.queue, ...state.activeJobs]);
      const filteredJobs = Object.fromEntries(
        Object.entries(state.jobs).filter(([id]) => activeJobIds.has(id)),
      );

      return {
        ...state,
        jobs: filteredJobs,
      };
    }

    case 'PROCESS_QUEUE': {
      // Start queued jobs if we have capacity
      const availableSlots = state.maxConcurrentJobs - state.activeJobs.length;
      console.log(
        `[ImportProgressContext] PROCESS_QUEUE: ${availableSlots} slots available, ${state.queue.length} jobs queued`,
      );

      if (availableSlots <= 0 || state.queue.length === 0) {
        console.log('[ImportProgressContext] No processing needed');
        return state;
      }

      const jobsToStart = state.queue.slice(0, availableSlots);
      const remainingQueue = state.queue.slice(availableSlots);

      console.log(
        `[ImportProgressContext] Moving jobs to active: ${jobsToStart.join(', ')}`,
      );
      console.log(
        `[ImportProgressContext] Remaining queue: ${remainingQueue.join(', ')}`,
      );

      return {
        ...state,
        queue: remainingQueue,
        activeJobs: [...state.activeJobs, ...jobsToStart],
        jobs: Object.fromEntries(
          Object.entries(state.jobs).map(([id, job]) => [
            id,
            jobsToStart.includes(id)
              ? { ...job, status: 'importing' } // Don't set startedAt here - let useEffect handle it
              : job,
          ]),
        ),
      };
    }

    default:
      return state;
  }
}

export function ImportProgressProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(importProgressReducer, initialState);
  const lastEmitRef = React.useRef<Record<string, number>>({});

  // Process queue when state changes
  useEffect(() => {
    log.debug(
      `[ImportProgressContext] Queue check: ${state.queue.length} queued, ${state.activeJobs.length}/${state.maxConcurrentJobs} active`,
    );
    if (
      state.queue.length > 0 &&
      state.activeJobs.length < state.maxConcurrentJobs
    ) {
      log.debug('[ImportProgressContext] Processing queue...');
      dispatch({ type: 'PROCESS_QUEUE' });
    }
  }, [state.queue, state.activeJobs, state.maxConcurrentJobs]);

  // Auto-start jobs when they're moved from queue to active
  useEffect(() => {
    log.debug(
      '[ImportProgressContext] Checking for jobs to start...',
      state.activeJobs,
    );

    const jobsToStart = state.activeJobs.filter((jobId) => {
      const job = state.jobs[jobId];
      const needsStart = job?.status === 'importing' && !job.startedAt;
      log.debug(
        `[ImportProgressContext] Job ${jobId}: status=${job?.status}, startedAt=${job?.startedAt}, needsStart=${needsStart}`,
      );
      return needsStart;
    });

    log.debug(
      `[ImportProgressContext] Found ${jobsToStart.length} jobs to start`,
    );

    jobsToStart.forEach((jobId) => {
      const job = state.jobs[jobId];
      if (job && job.type === 'folder' && job.path) {
        log.debug(
          `[ImportProgressContext] Starting folder import for: ${job.path}`,
        );

        // Mark as started and update progress
        dispatch({
          type: 'UPDATE_JOB_STATUS',
          payload: {
            id: jobId,
            status: 'importing',
          },
        });

        dispatch({
          type: 'UPDATE_JOB_PROGRESS',
          payload: {
            id: jobId,
            progress: {
              ...job.progress,
              phase: 'starting',
              message: 'Starting import...',
            },
          },
        });

        log.debug(
          '[ImportProgressContext] Triggering IPC import for:',
          job.path,
        );

        // Trigger the actual import through IPC
        run('track-import/import-from-source', {
          source: {
            type: 'folder',
            name: job.name,
            paths: [job.path],
          },
          jobId,
        });
      }
    });
  }, [state.activeJobs, state.jobs]);

  // Keep latest jobs in a ref so the IPC listener can be registered once
  const jobsRef = useRef(state.jobs);
  useEffect(() => {
    jobsRef.current = state.jobs;
  }, [state.jobs]);

  // Bridge window events → IPC for pause/cancel/resume controls
  useEffect(() => {
    const handlePause = (e: any) => {
      try {
        run('track-import/pause', e?.detail || {});
      } catch {}
    };
    const handleCancel = (e: any) => {
      try {
        run('track-import/cancel', e?.detail || {});
        const id = e?.detail?.id;
        if (id) {
          dispatch({ type: 'CANCELLING_JOB', payload: { id } });
        }
      } catch {}
    };
    const handleResume = (e: any) => {
      try {
        run('track-import/resume', e?.detail || {});
      } catch {}
    };

    window.addEventListener('track-import/pause', handlePause as EventListener);
    window.addEventListener(
      'track-import/cancel',
      handleCancel as EventListener,
    );
    window.addEventListener(
      'track-import/resume',
      handleResume as EventListener,
    );

    return () => {
      window.removeEventListener(
        'track-import/pause',
        handlePause as EventListener,
      );
      window.removeEventListener(
        'track-import/cancel',
        handleCancel as EventListener,
      );
      window.removeEventListener(
        'track-import/resume',
        handleResume as EventListener,
      );
    };
  }, []);
  useEffect(() => {
    const CANCEL_TIMEOUT_MS = 15000;
    const interval = setInterval(() => {
      const now = Date.now();
      Object.values(jobsRef.current).forEach((job) => {
        if (job.status === 'cancelling') {
          const cancellingFor = now - (job.startedAt || job.addedAt);
          if (cancellingFor > CANCEL_TIMEOUT_MS) {
            log.warn(
              `[ImportProgressContext] Job ${job.id} stuck in cancelling for ${cancellingFor}ms — forcing cancelled`,
            );
            dispatch({
              type: 'UPDATE_JOB_STATUS',
              payload: { id: job.id, status: 'cancelled' },
            });
            dispatch({ type: 'COMPLETE_JOB', payload: { id: job.id } });
            try {
              window.dispatchEvent(
                new CustomEvent('library-refresh', {
                  detail: { reason: 'import-job-cancelled', path: job.path },
                }),
              );
            } catch {}
          }
        }
      });
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Listen for global import progress events (register once)
  useEffect(() => {
    const handleProgress = (progressData: any) => {
      log.debug('[ImportProgressContext] Received progress:', progressData);

      const allJobs = Object.values(jobsRef.current);
      const jobIdMatch = progressData.jobId
        ? allJobs.find((job) => job.id === progressData.jobId)
        : undefined;
      const activeMatch = allJobs.find((job) => {
        const isActive =
          job.status === 'importing' ||
          job.status === 'queued' ||
          job.status === 'cancelling';
        const pathMatches =
          job.path &&
          (progressData.source?.path === job.path ||
            progressData.source?.paths?.includes(job.path));
        return isActive && pathMatches;
      });
      const cancelledFallback =
        !activeMatch && progressData.phase === 'cancelled'
          ? allJobs.find((job) => {
              return (
                job.status === 'cancelled' &&
                job.path &&
                (progressData.source?.path === job.path ||
                  progressData.source?.paths?.includes(job.path))
              );
            })
          : undefined;

      const matchingJob = jobIdMatch || activeMatch || cancelledFallback;

      log.debug(
        `[ImportProgressContext] Job match: jobIdMatch=${jobIdMatch?.id}, activeMatch=${activeMatch?.id}, chosen=${matchingJob?.id}`,
      );

      if (matchingJob) {
        // Throttle UI updates per job to reduce render pressure during multi-folder rescans
        const now = Date.now();
        const last = lastEmitRef.current[matchingJob.id] || 0;
        const shouldEmit =
          progressData.phase === 'complete' ||
          progressData.phase === 'error' ||
          progressData.phase === 'cancelled' ||
          now - last >= 200;
        if (!shouldEmit) return;
        lastEmitRef.current[matchingJob.id] = now;
        log.debug(
          `[ImportProgressContext] Found matching job: ${matchingJob.id}`,
        );

        dispatch({
          type: 'UPDATE_JOB_PROGRESS',
          payload: {
            id: matchingJob.id,
            progress: {
              phase: progressData.phase,
              totalTracks: progressData.totalTracks || 0,
              processedTracks: progressData.processedTracks || 0,
              message: progressData.message || '',
              created: progressData.created,
              merged: progressData.merged,
              errors: progressData.errors,
              startTime: progressData.startTime,
              elapsed: progressData.elapsed,
              totalDirectories: (progressData as any).totalDirectories,
            },
          },
        });

        // Update status based on phase
        if (progressData.phase === 'complete') {
          log.info(`[ImportProgressContext] Completing job: ${matchingJob.id}`);
          dispatch({
            type: 'UPDATE_JOB_STATUS',
            payload: { id: matchingJob.id, status: 'complete' },
          });
          dispatch({ type: 'COMPLETE_JOB', payload: { id: matchingJob.id } });
          try {
            window.dispatchEvent(
              new CustomEvent('library-refresh', {
                detail: {
                  reason: 'import-job-complete',
                  path: matchingJob.path,
                },
              }),
            );
          } catch {}
        } else if (progressData.phase === 'error') {
          log.warn(
            `[ImportProgressContext] Error in job: ${matchingJob.id}`,
            progressData.error,
          );
          dispatch({
            type: 'UPDATE_JOB_STATUS',
            payload: {
              id: matchingJob.id,
              status: 'error',
              error: progressData.error || 'Import failed',
            },
          });
          dispatch({ type: 'COMPLETE_JOB', payload: { id: matchingJob.id } });
          try {
            window.dispatchEvent(
              new CustomEvent('library-refresh', {
                detail: { reason: 'import-job-error', path: matchingJob.path },
              }),
            );
          } catch {}
        } else if (progressData.phase === 'cancelled') {
          log.info(
            `[ImportProgressContext] Cancelled job confirmed by backend: ${matchingJob.id}`,
          );
          dispatch({
            type: 'UPDATE_JOB_STATUS',
            payload: { id: matchingJob.id, status: 'cancelled' },
          });
          dispatch({ type: 'COMPLETE_JOB', payload: { id: matchingJob.id } });
          try {
            window.dispatchEvent(
              new CustomEvent('library-refresh', {
                detail: {
                  reason: 'import-job-cancelled',
                  path: matchingJob.path,
                },
              }),
            );
          } catch {}
        }
      } else {
        log.debug(
          '[ImportProgressContext] No matching job found for progress update',
        );
        log.debug(
          '[ImportProgressContext] Available jobs:',
          Object.values(state.jobs).map((j) => ({
            id: j.id,
            path: j.path,
            status: j.status,
          })),
        );
      }
    };

    on('track-import/progress', handleProgress);

    return () => {
      removeListener('track-import/progress');
    };
  }, []);

  const addImportJob = (job: Omit<ImportJob, 'id' | 'addedAt'>): string => {
    const id = `import-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    console.log(`[ImportProgressContext] Adding job with ID: ${id}`, job);

    dispatch({ type: 'ADD_JOB', payload: job });

    console.log(
      `[ImportProgressContext] Job added to queue. Current state will update next.`,
    );
    return id;
  };

  const getJobById = (id: string): ImportJob | undefined => {
    return state.jobs[id];
  };

  const getActiveJobs = (): ImportJob[] => {
    return state.activeJobs.map((id) => state.jobs[id]).filter(Boolean);
  };

  const getJobsForPath = (path: string): ImportJob[] => {
    const normalize = (p?: string) =>
      (p || '').replace(/\\/g, '/').replace(/\/+$/g, '').toLowerCase();
    const target = normalize(path);
    return Object.values(state.jobs).filter(
      (job) => normalize(job.path) === target,
    );
  };

  return (
    <ImportProgressContext.Provider
      value={{
        state,
        dispatch,
        addImportJob,
        getJobById,
        getActiveJobs,
        getJobsForPath,
      }}
    >
      {children}
    </ImportProgressContext.Provider>
  );
}

export function useImportProgress() {
  const context = useContext(ImportProgressContext);
  if (!context) {
    throw new Error(
      'useImportProgress must be used within an ImportProgressProvider',
    );
  }
  return context;
}
